import React, { useState, useMemo, useEffect } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import { Header, Segment, Grid, Modal, Divider, List } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import AssociateSideBar from '../Sidebar/AssociateSideBar'
import AssociateHeader from 'components/Header/AssociateHeader'
import ModuleNotFound from 'routes/error/moduleNotFound'
import { initSocketHandlers } from 'utils/socket/chat/handlers'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useTabNavigation, ViewRegistrar } from 'hooks/useTabNavigation'

const onlySuperAdminModules = [
	'/process_catalog',
	'/process_catalog/:id',
	'/associates',
	'/associates/:id',
	'/modules_dashboard',
	'/modules_dashboard/:id',
	'/wizards_superAdmin',
	'/wizards_superAdmin/:id',
	'/impression_formats_superAdmin',
	'/impression_formats_superAdmin/:id',
	'/procedures_superAdmin',
	'/procedures_superAdmin/:id',
	'/communications_superAdmin',
	'/communications_superAdmin/:id',
	'/fuel_supply_template',
	'/fuel_supply_template/:id',
	'/partner_billing',
	'/partner_billing/:id',
	'/query_executor',
]

const PrivateAssociateRouteContent = ({
	Component,
	routeProps,
	sidebarOptions,
	isAuthenticated,
	hasSuperAdminAccess,
	associateUser,
	associate,
	logout,
	lang,
	rest,
}) => {
	const { registerActiveView, openTab } = useTabNavigation()
	const {
		setExpand,
		previousExpand,
		setPreviousExpand,
		mobileOnly,
		setMobileOnly,
	} = useLateralMenu()
	const [openModal, setOpenModal] = useState(false)
	const [sidebarMenuOptions, setSidebarMenuOptions] = useState(null)
	const handleSidebarMenuOpen = (options) => setSidebarMenuOptions(options)

	const memoizedRouteProps = useMemo(
		() => ({ ...routeProps, sidebarOptions: sidebarMenuOptions || sidebarOptions }),
		[routeProps, sidebarOptions, sidebarMenuOptions]
	)

	const memoizedLayoutConfig = useMemo(
		() => ({
			Header: AssociateHeader,
			Sidebar: AssociateSideBar,
			sidebarWidth: 225,
			sidebarProps: {
				user: associateUser,
				associate: associate,
				rest: rest,
				setExpand,
				previousExpand,
				setPreviousExpand,
				openModal,
				setOpenModal,
				mobileOnly,
				setMobileOnly,
				lang,
				onMenuOpen: handleSidebarMenuOpen,
				onTabOpen: (route) => openTab(route, routeProps.history.push, false),
			},
			layoutProps: { logout, lang, rest },
		}),
		[
			associateUser,
			associate,
			logout,
			rest,
			setExpand,
			previousExpand,
			setPreviousExpand,
			openModal,
			mobileOnly,
			setMobileOnly,
			lang,
			handleSidebarMenuOpen,
			openTab,
			routeProps.history.push,
		]
	)

	if (!isAuthenticated) return <Redirect to='/login' />
	if (!hasSuperAdminAccess) return <ModuleNotFound />

	return (
		<>
			<ViewRegistrar
				registerActiveView={registerActiveView}
				Component={Component}
				routeProps={memoizedRouteProps}
				layoutConfig={memoizedLayoutConfig}
			/>
			<Modal
				closeIcon
				basic
				centered={true}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				open={openModal}
				size='large'
			>
				<Modal.Content>
					<Segment inverted style={{ height: '100%' }}>
						<Grid stackable divided inverted>
							<Grid.Row columns={2}>
								<Grid.Column width={6} verticalAlign='middle' textAlign='center'>
									<Header inverted content='(c) Geologistic GPS 2025' />
								</Grid.Column>
								<Grid.Column width={8}>
									<Header inverted content='Contacto' />
									<Divider />
									<Header
										size='small'
										inverted
										content='¿Necesitas registrar un nuevo ticket o solicitud?'
									/>
									<List divided inverted>
										<List.Item
											header='Ingresa al Portal: '
											content='https://geologistic.com.mx/Soporte/'
											style={{ cursor: 'pointer' }}
											onClick={() => {
												window.open(
													'https://geologistic.com.mx/Soporte/',
													'_blank'
												)
											}}
										/>
										<List.Item
											header='Soporte'
											content='(686) 214 5447, (686) 385 6938,  (686) 510 9915'
										/>
										<List.Item header='Oficina' content='(686) 372 9877' />
										<List.Item
											header='Emergencias'
											content='(686) 420 3333, (686) 214 5447, (686) 385 6938, (686) 510 9915,  (686) 123 6454'
										/>
										<List.Item header='Administracion' content='(686) 122 0316' />
										<List.Item
											header='Ventas'
											content='(686) 510 9915, (686) 196 4736, (686) 420 8586,  (686) 191 1571'
										/>
									</List>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</Segment>
				</Modal.Content>
			</Modal>
		</>
	)
}

const PrivateAssociateRoute = (props) => {
	const { t } = useTranslation()
	const { isAuthenticated = true, component: Component, lang, ...rest } = props

	const dispatch = useDispatch()

	const modules = useMemo(
		() => [
			{
				name: t('Administrador de Catalogos'),
				icon: 'archive',
				module_group: 'SuperAdmin',
				url: 'process_catalog',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Administrador de Clientes'),
				icon: 'archive',
				module_group: 'Clientes',
				url: 'sys_company_administrator',
				isVisible: true,
				permisitions: { ACCESS: true },
			},
			{
				name: t('Dashboard de Módulos'),
				icon: 'block layout',
				module_group: 'Dashboard',
				url: 'modules_dashboard',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Socios'),
				icon: 'users',
				module_group: 'SuperAdmin',
				url: 'associates',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Wizard'),
				icon: 'archive',
				module_group: 'SuperAdmin',
				url: 'wizards_superAdmin',
				isVisible: true,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Formato de Impresión'),
				icon: 'archive',
				module_group: 'SuperAdmin',
				url: 'impression_formats_superAdmin',
				isVisible: true,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Procedimientos'),
				icon: 'archive',
				module_group: 'SuperAdmin',
				url: 'procedures_superAdmin',
				isVisible: true,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Comunicados'),
				icon: 'archive',
				module_group: 'SuperAdmin',
				url: 'communications_superAdmin',
				isVisible: true,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Plantillas de Suministros'),
				icon: 'tint',
				module_group: 'Suministros',
				url: 'fuel_supply_template',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Facturación de Socios'),
				icon: 'file alternate',
				module_group: 'SuperAdmin',
				url: 'partner_billing',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
			{
				name: t('Ejecutor de Queries'),
				icon: 'terminal',
				module_group: 'Ejecutor de Queries',
				url: 'query_executor',
				isVisible: props.associate?.super_admin,
				permisitions: { ACCESS: props.associate?.super_admin },
			},
		],
		[props.associate?.super_admin, t]
	)

	const associateUser = useMemo(() => {
		if (!props.associate) return props.associate

		const currentModules = Array.isArray(props.associate.modules) ? props.associate.modules : []
		const knownUrls = new Set(currentModules.map((module) => module?.url).filter(Boolean))
		const mergedModules = [...currentModules]

		modules.forEach((module) => {
			if (!knownUrls.has(module.url)) {
				mergedModules.push(module)
			}
		})

		return {
			...props.associate,
			modules: mergedModules,
		}
	}, [modules, props.associate])

	const hasSuperAdminAccess = useMemo(() => {
		const routePath = rest.path || ''
		return !onlySuperAdminModules.includes(routePath) || !!props.associate?.super_admin
	}, [props.associate?.super_admin, rest.path])

	useEffect(() => {
		if (!props.associate) return
		dispatch(initSocketHandlers())
	}, [dispatch, props.associate])

	return (
		<Route
			{...rest}
			render={(routeProps) => (
				<PrivateAssociateRouteContent
					Component={Component}
					routeProps={routeProps}
					isAuthenticated={isAuthenticated}
					hasSuperAdminAccess={hasSuperAdminAccess}
					associateUser={associateUser}
					associate={props.associate}
					logout={rest.logout}
					lang={lang}
					rest={rest}
				/>
			)}
		/>
	)
}

const mapStateToProps = (state) => ({
	isAuthenticated: !!state.app?.superAdmin?.associate,
	associate: state.app?.superAdmin?.associate,
	lang: state.app?.lang,
})

export default connect(mapStateToProps, null)(PrivateAssociateRoute)

const EmptyComponent = () => null
